import { ButtonRow, Form } from '@ksyos/design-system';
import { isBoolean } from 'lodash';
import { useEffect, useState } from 'react';
import type { OnChangeEventOrManualInput } from '@frontend/utils/hooks/useForm';
import { FormProvider } from '@frontend/components/contexts/form/FormProvider';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import type {
    IOphthalmologyAsset,
    ITfoDiagnosticReportCustomControlData,
    TfoV1ProcedureDiagnosticReportAddEditDraftSchema,
    TfoV1ProcedureDiagnosticReportAddEditFinalSchema,
} from '../../../../../../common/validation/customControls/ophthalmology';
import type { ICustomControlProcedureDiagnosticReportAddEditProps } from '../../interfaces/';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import {
    tfoV1ProcedureDiagnosticReportAddEditDraftSchema,
    tfoV1ProcedureDiagnosticReportAddEditFinalSchema,
} from '../../../../../../common/validation/customControls/ophthalmology';
import { getSelectedLanguage } from '../../../../../translate';
import { renameErrorKeys } from '../../../../../utils/renameErrorKeys';
import { showServiceToast } from '../../../../../utils/showToast';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ProceedWithOnlyAdditionalFiles } from '../../../../elements/ProceedWithOnlyAdditionalFiles';
import { ProceedWithoutFiles } from '../../../../elements/ProceedWithoutFiles';
import { NewOrEditDiagnosticReportProcessContainer } from '../../utils/v1/NewOrEditDiagnosticReportProcessContainer';
import {
    TfoV1DiagnosticReportAddEditCustomControlFields,
    tfoV1DiagnosticReportInitializeCustomControlData,
} from './TfoV1DiagnosticReportAddEditCustomControlFields';
import { TfoV1ProcedureAddEdit } from './TfoV1ProcedureAddEdit';

export type TfoV1ProcedureDiagnosticReportAddEditFormState =
    | TfoV1ProcedureDiagnosticReportAddEditFinalSchema
    | TfoV1ProcedureDiagnosticReportAddEditDraftSchema;

export function TfoV1ProcedureDiagnosticReportAddEdit(
    props: Readonly<ICustomControlProcedureDiagnosticReportAddEditProps>
) {
    const [initialValues, setInitialValues] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [showProceedWithoutFiles, setShowProceedWithoutFiles] = useState(false);
    const [showProceedWithOnlyAdditionalFiles, setShowProceedWithOnlyAdditionalFiles] = useState(false);

    const translate = getSelectedLanguage();

    useEffect(() => {
        setInitialValues({
            ...tfoV1DiagnosticReportInitializeCustomControlData(),
            ...props.procedure.customControlData,
            ...props.diagnosticReport.customControlData,
            draftCareProviderOrganizationId: props.procedure.draftCareProviderOrganizationId,
            draftCareProviderPractitionerId: props.procedure.draftCareProviderPractitionerId,
        });
    }, []);

    return (
        <>
            <FormProvider
                draftValidationSchema={tfoV1ProcedureDiagnosticReportAddEditDraftSchema}
                initialValues={initialValues}
                onChange={async (option) => {
                    const formattedValue =
                        option.value !== null && !isBoolean(option.value) ? `${option.value}` : option.value;
                    await props.handleChangeCustomControl({ [option.name]: formattedValue });
                    await props.validateOneCustomControlDiagnosticReport(option.name, 'customControlData.');
                }}
                onSubmit={async (values, saveType) => {
                    if (!saveType) {
                        return;
                    }

                    await props.handleChangeCustomControlDiagnosticReport({
                        ...values,
                        files: props.procedure.customControlData.files, // Files are managed outside of the FormContext
                    });

                    await props.handleChangeCustomControl({
                        ...values,
                        files: props.procedure.customControlData.files, // Files are managed outside of the FormContext
                    });

                    await saveProcedure(saveType);
                }}
                validationSchema={tfoV1ProcedureDiagnosticReportAddEditFinalSchema}
            >
                {(form) => {
                    return (
                        <Form
                            onReset={form.handleReset}
                            onSubmit={form.handleSubmit}
                            submitOnEnter={false}
                        >
                            <TfoV1ProcedureAddEdit
                                {...props}
                                assets={props.procedure.customControlData.files}
                                errors={renameErrorKeys(form.errors.validationErrors, '', 'customControlData.')}
                                handleChangeCustomControl={async (values) => {
                                    const key = Object.keys(
                                        values
                                    )[0] as keyof TfoV1ProcedureDiagnosticReportAddEditFormState; // always changing 1 prop at a time, so getting `[0]` is safe

                                    // Files are managed outside of the FormContext, so we need to handle them separately
                                    if (key === 'files') {
                                        await props.handleChangeCustomControl({
                                            [key]: values[key as keyof typeof values],
                                        });
                                    } else {
                                        form.handleChange({
                                            name: key,
                                            value: values[key as keyof typeof values],
                                        } as OnChangeEventOrManualInput<TfoV1ProcedureDiagnosticReportAddEditFormState>);
                                    }
                                }}
                                isBioGrader
                                itemState={props.itemState}
                                onUploadingFiles={setIsButtonDisabled}
                                procedure={{
                                    ...props.procedure,
                                    customControlData: form.values,
                                }}
                                removeProcedure={props.removeProcedure}
                            />

                            <NewOrEditDiagnosticReportProcessContainer
                                currentDiagnosticReport={props.currentDiagnosticReport}
                                itemState={props.itemState}
                                process={props.process}
                                service={props.service}
                            >
                                <TfoV1DiagnosticReportAddEditCustomControlFields
                                    customControlData={form.values as ITfoDiagnosticReportCustomControlData}
                                    isBiograder={true}
                                    showAdvisedPolicy={true}
                                    showToelichting={true}
                                />
                                <ButtonRow justify="right">
                                    <CancelButton
                                        cancel={props.onCancel}
                                        entity={ProcessEntity.procedure}
                                        isExisting={!!props.request.id}
                                        revertChanges={props.revertProcedureChanges}
                                        serviceCode={props.service.code}
                                        serviceType={props.service.serviceType}
                                    />
                                    <SaveButton
                                        buttonDisabled={isButtonDisabled}
                                        save={async () => await form.handleSubmit('draft', true)}
                                    />
                                    <SaveSendButton
                                        buttonDisabled={isButtonDisabled}
                                        saveSend={async () => await form.handleSubmit('final', true)}
                                    />
                                </ButtonRow>
                            </NewOrEditDiagnosticReportProcessContainer>
                        </Form>
                    );
                }}
            </FormProvider>

            {showProceedWithoutFiles && (
                <ProceedWithoutFiles
                    entity={ProcessEntity.procedure}
                    onNo={() => {
                        setIsButtonDisabled(false);
                        setShowProceedWithoutFiles(false);
                    }}
                    onYes={async () => {
                        setShowProceedWithoutFiles(false);

                        await saveProcedure('final', true);
                    }}
                    serviceType={props.service.serviceType}
                />
            )}
            {showProceedWithOnlyAdditionalFiles && (
                <ProceedWithOnlyAdditionalFiles
                    onNo={() => {
                        setShowProceedWithOnlyAdditionalFiles(false);
                        setIsButtonDisabled(false);
                    }}
                    onYes={async () => {
                        setShowProceedWithOnlyAdditionalFiles(false);

                        await saveProcedure('final', undefined, true);
                    }}
                />
            )}
        </>
    );

    async function saveProcedure(
        saveType: 'draft' | 'final',
        canProceedWithoutFiles?: boolean,
        canProceedWithOnlyAdditionalFiles?: boolean
    ) {
        setIsButtonDisabled(true);

        const validatedProcedure = await props.validateProcedure(saveType);

        if (!validatedProcedure) {
            setIsButtonDisabled(false);

            return;
        }

        if (saveType === 'final') {
            if (
                !canProceedWithoutFiles &&
                (!props.procedure.customControlData.files || props.procedure.customControlData.files.length === 0)
            ) {
                setShowProceedWithoutFiles(true);

                return;
            } else if (
                !canProceedWithOnlyAdditionalFiles &&
                props.procedure.customControlData.files.length > 0 &&
                props.procedure.customControlData.files.every(
                    (file: IOphthalmologyAsset) => file.placement === 'aanvullend'
                )
            ) {
                setShowProceedWithOnlyAdditionalFiles(true);

                return;
            }
        }

        const requestId = await props.onSaveProcedure(saveType);

        if (!requestId) {
            setIsButtonDisabled(false);

            return;
        }

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'procedure',
                translate.toast.submitDefaultMessage
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'procedure',
                translate.toast.draftDefaultMessage
            );
        }

        await props.onSave(requestId);
    }
}
